/* eslint-disable import/first */
import React, { useState, useEffect, useRef, createRef } from "react";
import "suneditor/dist/css/suneditor.min.css";
import SunEditor, { buttonList } from "suneditor-react";
import axios from "axios";
import { useSelector } from "react-redux";
import Page404 from "app/not-found";
import { Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import "../../styles/styles.css";

registerLocale("fr", fr);

// ---- Styles ----
const UploadProfile = styled(Upload)`
  .ant-upload {
    width: 50px !important;
    height: 50px !important;
  }
`;

// ---- API ----
const API_URL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// =====================================================
//                COMPONENT
// =====================================================
const PageModifierArticle = () => {
  const { user } = useSelector((state) => state.user);

  const [articles, setArticles] = useState([]);
  const [categorie, setCategorie] = useState("Articles");
  const [articleSelected, setArticleSelected] = useState(-1);

  const [titre, setTitre] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [createdAt, setCreatedAt] = useState(null);

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  const [editorContent, setEditorContent] = useState("");
  const editorRef = createRef();
  const editor = useRef(null);

  const getSunEditorInstance = (instance) => {
    if (instance) editor.current = instance;
  };

  // -----------------------------------------------------
  // FETCH ARTICLES
  // -----------------------------------------------------
  useEffect(() => {
    if (user) {
      const fetchArticles = async () => {
        try {
          const res = await axiosInstance.get("api/articles");
          setArticles(res.data);
        } catch (err) {
          console.log("Erreur fetch articles:", err);
        }
      };
      fetchArticles();
    }
  }, [user]);

  const articlesParCategorie = articles.filter((a) => a.categorie === categorie);

  // -----------------------------------------------------
  // CHANGE CATEGORY
  // -----------------------------------------------------
  const changeCategorie = (e) => {
    setCategorie(e.target.value);
    setArticleSelected(-1);
    resetForm();
  };

  // -----------------------------------------------------
  // CHANGE ARTICLE
  // -----------------------------------------------------
  const changeArticle = (e) => {
    const idx = e.target.value;
    setArticleSelected(idx);

    if (idx === "-1") {
      resetForm();
    } else {
      const art = articlesParCategorie[idx];
      setTitre(art.titre);
      setDescription(art.description);
      setDate(art.date);
      setCreatedAt(art.createdAt ? new Date(art.createdAt) : null);
      setAvatarPreview(art.image);
      setEditorContent(art.html);

      setTimeout(() => {
        try {
          editor.current.setContents(art.html);
        } catch (err) {
          console.log("Erreur setContents:", err);
        }
      }, 50);
    }
  };

  // -----------------------------------------------------
  // RESET FORM
  // -----------------------------------------------------
  const resetForm = () => {
    setTitre("");
    setDescription("");
    setDate("");
    setCreatedAt(null);
    setAvatarPreview(null);
    setEditorContent("");
    if (editor.current) editor.current.setContents("");
  };

  // -----------------------------------------------------
  // UPLOAD AVATAR VERS LE SERVEUR
  // -----------------------------------------------------
  const handleAvatarChange = async (info) => {
    const file = info.file.originFileObj;
    if (!file || file === avatarFile) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarPreview(res.data); // serveur renvoie { url: "..." }
      setAvatarFile(file);
    } catch (err) {
      console.log("Erreur upload avatar:", err);
    }
  };

  // -----------------------------------------------------
  // UPLOAD IMAGE SUNEDITOR VERS LE SERVEUR
  // -----------------------------------------------------
  const onImageUploadBefore = async (files, info, uploadHandler) => {
    const file = files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      uploadHandler({
        result: [
          {
            url: res.data,
            name: file.name,
            size: file.size,
          },
        ],
      });
    } catch (err) {
      console.log("Erreur upload image SunEditor:", err);
    }
  };

  // -----------------------------------------------------
  // SAVE ARTICLE
  // -----------------------------------------------------
  const handleSave = async () => {
    if (!titre || !description || !date || !editorContent || !avatarPreview) {
      alert("Vous devez entrer tous les champs");
      return;
    }

    const art = articlesParCategorie[articleSelected];

    try {
      await axiosInstance.post("api/articles", {
        href: art.href,
        titre,
        description,
        date,
        createdAt: createdAt ? createdAt.toISOString() : null,
        html: editorContent,
        image: avatarPreview,
        categorie,
      });

      alert("Article mis à jour !");
    } catch (err) {
      console.log("Erreur update article:", err);
      alert("Erreur lors de la sauvegarde");
    }
  };

  if (!user) return <Page404 />;

  // =====================================================
  //                 RENDER
  // =====================================================
  return (
    <div style={{ padding: 20 }}>
      <table>
        <tbody>
          <tr>
            <td>Catégorie:</td>
            <td>
              <select value={categorie} onChange={changeCategorie}>
                <option value="Articles">Articles</option>
                <option value="Projets">Projets</option>
                <option value="Propositions">Propositions</option>
                <option value="Évenements">Événements</option>
                <option value="Conseils">Conseils d'arrondissement</option>
                <option value="Photos">Photos</option>
                <option value="Médias">Médias</option>
              </select>
            </td>

            <td>Article:</td>
            <td colSpan="3">
              <select value={articleSelected} onChange={changeArticle} style={{width:"510px"}}>
                <option value="-1">Sélectionnez un article</option>
                {articlesParCategorie.map((a, i) => (
                  <option key={i} value={i}>
                    {a.titre}
                  </option>
                ))}
              </select>
            </td>

            <td>Image:</td>
            <td>
              <UploadProfile
                name="avatar"
                listType="picture-circle"
                showUploadList={false}
                beforeUpload={() => true}
                onChange={handleAvatarChange}
                accept="image/*"
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar"
                    style={{
                      width: 50,
                      height: 50,
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Envoyer</div>
                  </div>
                )}
              </UploadProfile>
            </td>
          </tr>

          {/* ==== TITLE / DATE / CREATEDAT ==== */}
          <tr>
            <td>Titre:</td>
            <td>
              <input
                type="text"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                style={{ width: "100%" }}
              />
            </td>

            <td>Date:</td>
            <td>
              <input
                type="text"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{ width: "100%" }}
              />
            </td>

            <td>Créé le :</td>
            <td>
              <DatePicker
                selected={createdAt}
                onChange={(date) => setCreatedAt(date)}
                locale="fr"
                dateFormat="dd-MM-yyyy"
                placeholderText="Date de création"
                className="form-control"
              />
            </td>
          </tr>

          <tr>
            <td>Description:</td>
            <td colSpan={5}>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ width: "100%" }}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {/* ==== SUNEDITOR ==== */}
      <SunEditor
        ref={editorRef}
        getSunEditorInstance={getSunEditorInstance}
        setAllPlugins={true}
        setOptions={{
          buttonList: buttonList.complex,
          iframe: false,
          defaultTag: "p"
        }}
        onImageUploadBefore={onImageUploadBefore}
        height="400px"
        onChange={setEditorContent}
        
      />

      <div style={{ marginTop: 20, textAlign: "left" }}>
        <button
          onClick={handleSave}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Enregistrer l'article
        </button>
      </div>
    </div>
  );
};

export default PageModifierArticle;
